import useStyles from 'isomorphic-style-loader/useStyles';
import type { ReactNode, ComponentType, PropsWithChildren } from 'react';
import { useMemo } from 'react';
import type { StrictButtonProps } from 'semantic-ui-react';
import { Button, Icon, Message } from 'semantic-ui-react';

import s from './table-no-data.scss';

export type NoDataCustomComponentProps = {
  filterStr?: string;
  loading?: boolean;
  descriptors: {
    container?: string;
    data?: string;
  };
  ctaButton: ReactNode;
};

export type ReactTableNoDataCustomComponent =
  | ComponentType<NoDataCustomComponentProps>
  | {
      NoDataMessage: ComponentType<NoDataCustomComponentProps>;
      LoadingMessage?: ComponentType<NoDataCustomComponentProps>;
      FilteredNoDataMessage?: ComponentType<NoDataCustomComponentProps>;
    };

export type ReactTableNoDataProps = Omit<
  NoDataCustomComponentProps,
  'ctaButton'
> & {
  CustomComponent?: ReactTableNoDataCustomComponent;
  ctaButtonProps?: Omit<StrictButtonProps, 'primary' | 'size' | 'compact'> & {
    content: ReactNode;
    id?: string;
  };
};

/**
 * @deprecated Legacy empty-state container; use `EmptyPlaceholder` from
 * `@components/empty-placeholder`.
 */
export const ReactTableNoDataMessageContainer = ({
  children,
}: PropsWithChildren) => {
  useStyles(s);

  return (
    <div className={s.noDataWrapper}>
      <Message className={s.noDataMessage}>{children}</Message>
    </div>
  );
};

/**
 * @deprecated Superseded by `EmptyPlaceholder` (`@components/empty-placeholder`).
 */
export const ReactTableNoData = (props: ReactTableNoDataProps) => {
  const { CustomComponent, loading, filterStr, descriptors, ctaButtonProps } =
    props;

  const ctaButton = useMemo(
    () =>
      ctaButtonProps && ctaButtonProps.content ? (
        <Button primary size="tiny" compact {...ctaButtonProps} />
      ) : null,
    [ctaButtonProps],
  );

  const customComponentProps = { ...props, ctaButton };

  if (CustomComponent && typeof CustomComponent === 'function') {
    return <CustomComponent {...customComponentProps} />;
  }

  if (loading) {
    return (
      <ReactTableNoDataMessageContainer>
        {CustomComponent?.LoadingMessage ? (
          <CustomComponent.LoadingMessage {...customComponentProps} />
        ) : (
          'Loading...'
        )}
      </ReactTableNoDataMessageContainer>
    );
  }

  if (filterStr) {
    return (
      <ReactTableNoDataMessageContainer>
        {CustomComponent?.FilteredNoDataMessage ? (
          <CustomComponent.FilteredNoDataMessage {...customComponentProps} />
        ) : (
          <>
            <Icon name="frown" />
            No results for filter: <strong>{filterStr}</strong>
          </>
        )}
      </ReactTableNoDataMessageContainer>
    );
  }

  return (
    <ReactTableNoDataMessageContainer>
      {CustomComponent?.NoDataMessage ? (
        <CustomComponent.NoDataMessage {...customComponentProps} />
      ) : (
        <>
          {`Hmm, it looks like this ${descriptors?.container || 'table'} doesn't
          have any ${descriptors?.data || 'data'} yet... `}
          {ctaButton}
        </>
      )}
    </ReactTableNoDataMessageContainer>
  );
};
